import { VehicleMapDataPoint } from "../../api/types/VehicleMap";
import { statusCodeIsPinned } from "./statusCodeFilter";
import { APP_CONFIG } from "../../constants/constants";
import { TRIP_COLORS } from "./constants";
import { polylineConfigLineStyles } from "./map-elements/helper";
import { RenderRouteDefault } from "./map-elements/route-default";
import { RenderRouteDirectionsService } from "./map-elements/route-directionsservice";
import { RenderRoutePointToPoint } from "./map-elements/route-pointtopoint";
import { RenderRouteSnapToRoad } from "./map-elements/route-snaptoroad";
import { RenderTripPoints } from "./map-elements/trip-points";
import {
  TMapRouteViewsEnabled,
  TRenderRouteClassParams,
} from "./map-elements/type";
import {
  TMapOperations,
  TMapOperationsProps,
  TMapUpdatesHandler,
  TVehicleDataPoint,
} from "./type";

const debug = false;

export const mapOperations: TMapOperations = (props, filteredDataPoints) => {
  if (APP_CONFIG.DEBUG.MAPS)
    console.log("== MAPOP", props.dataPoints.length, filteredDataPoints.length);
  if (props.dataPoints.length === 0) return; // prevents the `Cannot read properties of null (reading 'getCurrentCrsViewport')` error
  //(window as any).map = props.mapRef.current;
  const google = (window as any).google;

  // create a namespace for map objects
  props.mapRef.current.objects = {
    mTripLayers: [],
    mDirectionsManagers: [],
    mTripsVisibility: [],
    mWaypoints: [],
    mPushpins: [],
    mClusterLayer: null,
    mTripsData: [],
  };

  const renderMapObjects = async () => {
    const MAX_WAYPOINTS = 25;
    const MAX_VIAPOINTS = 10;
    const G_MAX_WAYPOINTS = 25;
    const { AdvancedMarkerElement } =
      await google?.maps?.importLibrary("marker");
    const map = props.mapRef.current.map;
    // determine snapped points and save them to LS - to prevent repeated calls
    // finally use snapped points to create waypoints

    props.dataPoints
      // .filter((tripPoints) => tripPoints.length > 1) // disabled 6/30/2025, this line was filtering out trips with single trip point resulting in mismatched / incorrect trip points being displayed, opening trip 5 was displaying points from trip 6 - app-wide issue reported by client
      .forEach((tripPoints, tripIndex) => {
        if (APP_CONFIG.DEBUG.MAPS)
          console.log("===================== tripPoints", tripPoints.length);

        // initialize mTripsData for the trip index
        const viewInitValue = {
          classObject: null,
          error: false,
          mapElements: [],
          visible: true,
        };
        const { routeViewsEnabled } = props.mapRef.current;
        if (!props.mapRef.current.objects.mTripsData[tripIndex]) {
          props.mapRef.current.objects.mTripsData[tripIndex] = {
            tripIndex,
            originalPoints: tripPoints,
            views: {
              MARKERS: viewInitValue,
              ROUTE_POINT_TO_POINT: {
                ...viewInitValue,
                visible: routeViewsEnabled.POINT_TO_POINT,
              },
              ROUTE_SNAP_TO_ROAD: {
                ...viewInitValue,
                visible: routeViewsEnabled.SNAP_TO_ROAD,
              },
              ROUTE_DIRECTIONS_SERVICE: {
                ...viewInitValue,
                visible: routeViewsEnabled.DIRECTIONS_SERVICE,
              },
              ROUTE_DEFAULT: {
                ...viewInitValue,
                visible: routeViewsEnabled.DEFAULT,
              },
            },
          };
        }

        // render the trip route
        const commonParams: TRenderRouteClassParams = {
          props,
          tripIndex,
          sanitizedTripPoints: tripPoints,
          // interpolatedTripPoints: pointsInterpolated,
          polylineConfig: {
            ...polylineConfigLineStyles.SOLID_THIN,
            strokeColor: TRIP_COLORS[tripIndex],
          },
        };

        // trip points renderer
        const tripPointsRenderer = new RenderTripPoints(commonParams);
        props.mapRef.current.objects.mTripsData[
          tripIndex
        ].views.MARKERS.classObject = tripPointsRenderer;
        tripPointsRenderer.render();

        // route renderer - point to point (without snapping)
        const routeRendererP2P = new RenderRoutePointToPoint({
          ...commonParams,
          polylineConfig: {
            ...polylineConfigLineStyles.DASHED,
            strokeColor: TRIP_COLORS[tripIndex],
          },
        });
        props.mapRef.current.objects.mTripsData[
          tripIndex
        ].views.ROUTE_POINT_TO_POINT.classObject = routeRendererP2P;
        if (!props.sparseeventreporting) {
          routeRendererP2P.render();
        }

        // route renderer - snap to road
        const routeRendererSnapToRoad = new RenderRouteSnapToRoad(commonParams);
        props.mapRef.current.objects.mTripsData[
          tripIndex
        ].views.ROUTE_SNAP_TO_ROAD.classObject = routeRendererSnapToRoad;
        if (!props.sparseeventreporting) {
          routeRendererSnapToRoad.render();
        }
        // route renderer - directions service
        const routeRendererDirectionsService = new RenderRouteDirectionsService(
          {
            ...commonParams,
            polylineConfig: {
              ...polylineConfigLineStyles.DOTTED,
              strokeColor: TRIP_COLORS[tripIndex],
            },
          }
        );
        props.mapRef.current.objects.mTripsData[
          tripIndex
        ].views.ROUTE_DIRECTIONS_SERVICE.classObject =
          routeRendererDirectionsService;
        if (!props.sparseeventreporting) {
          routeRendererDirectionsService.render();
        }

        // route renderer - default
        const routeRendererDefault = new RenderRouteDefault({
          ...commonParams,
          polylineConfig: {
            ...polylineConfigLineStyles.SOLID_THICK,
            strokeColor: TRIP_COLORS[tripIndex],
          },
        });
        props.mapRef.current.objects.mTripsData[tripIndex].views.ROUTE_DEFAULT =
          {
            ...viewInitValue,
            classObject: routeRendererDefault,
          };
        if (!props.sparseeventreporting) {
          routeRendererDefault.render();
        }
      });

    // center the map to the polygon
    if (
      !!props.crashTrackImpactTimestamp &&
      !!APP_CONFIG.CRASHTRACK.HIGHLIGHT_EVENT_ON_INCIDENT_MAP
    ) {
      // do not fitBounds to the route because specific crash event
      // is being highlighted in `map-asset.tsx`
    } else {
      setTimeout(() => {
        // override map view if provided
        if (props.overrideMapView) {
          if (APP_CONFIG.DEBUG.MAPS)
            console.log("[assetMap] overrideMapView", props.overrideMapView);
          props.mapRef.current.map.setOptions({
            center: !!props.overrideMapView.center
              ? {
                  lat: props.overrideMapView.center.latitude,
                  lng: props.overrideMapView.center.longitude,
                }
              : undefined,
            zoom: props.overrideMapView.zoom,
            animate: true,
          });
          return;
        }
        // otherwise center to data points
        mapCenterToDataPoints(props, props.dataPoints);
      }, 1000);
    }

    return null;
  };
  renderMapObjects();
};

export const mapUpdatesHandler: TMapUpdatesHandler = async (
  props,
  action,
  value,
  additionalData
) => {
  const google = window.google;

  if (APP_CONFIG.DEBUG.MAPS /* && APP_CONFIG.DEBUG.VERBOSE*/) {
    console.log("[mapUpdatesHandler]", action, value);
  }

  switch (action) {
    case "centerToPushpin":
      if (value && value.length > 0) {
        mapCenterToDataPoints(props, value as VehicleMapDataPoint[][]);
        return;
      }

      const pushpinId = value as number;

      // Find the pushpin by ID
      const pushpinObject = props.mapRef.current.objects.mPushpins.find(
        (pushpinObject) => pushpinObject.recordId === pushpinId
      );

      if (pushpinObject) {
        props.mapRef.current.map.setOptions({
          center: new google.maps.LatLng(
            pushpinObject.marker.position.lat(),
            pushpinObject.marker.position.lng()
          ),
        });
        props.mapRef.current.map.setZoom(props.mapRef.current.map.getZoom()); // Maintain current zoom
      }
      break;

    case "centerToCoords":
      const coords = value as { center: number[]; zoom: number };
      if (coords.center.length === 2) {
        props.mapRef.current.map.setOptions({
          center: new google.maps.LatLng(coords.center[0], coords.center[1]),
        });
        props.mapRef.current.map.setZoom(coords.zoom ?? 5);
      }
      break;

    case "setTripHoverState":
      const tripIndex = value as number | null;

      // ================================= OLD CODE ABOVE THIS POINT
      const hoverStateRouteViewsEnabled: TMapRouteViewsEnabled =
        additionalData?.routeViewsEnabled || {
          DEFAULT: true,
          POINT_TO_POINT: true,
          SNAP_TO_ROAD: true,
          DIRECTIONS_SERVICE: true,
        };
      props.mapRef.current.routeViewsEnabled = hoverStateRouteViewsEnabled;

      // use the new renderRoute classObject to toggle route visibility based on selection
      props.mapRef.current?.objects?.mTripsData.forEach((tripData) => {
        const isSelected =
          tripIndex === null || tripData.tripIndex === tripIndex;

        // toggle pushpins visibility
        // props.mapRef.current?.objects?.mPushpins.forEach((pushpinObject) => {
        //   pushpinObject.marker.setMap(
        //     isSelected &&
        //       props.selectedStatusCodes.includes(
        //         pushpinObject.pointData.statuscode
        //       )
        //       ? props.mapRef.current.map
        //       : null
        //   );
        // });

        // toggle trip points/markers visibility using the classObject of RenderTripPoints
        const tripPointsRenderer = tripData.views.MARKERS
          .classObject as RenderTripPoints;
        if (tripPointsRenderer) {
          if (isSelected) {
            tripPointsRenderer.show();
          } else {
            tripPointsRenderer.hide();
          }
        }

        // toggle route visibility using the classObject of RenderRoutePointToPoint
        const routeRendererP2P = tripData.views.ROUTE_POINT_TO_POINT
          .classObject as RenderRoutePointToPoint;
        if (routeRendererP2P) {
          if (isSelected && hoverStateRouteViewsEnabled.POINT_TO_POINT) {
            routeRendererP2P.show();
          } else {
            routeRendererP2P.hide();
          }
        }

        // toggle snap to road route visibility using the classObject of RenderRouteSnapToRoad
        const routeRendererSnapToRoad = tripData.views.ROUTE_SNAP_TO_ROAD
          .classObject as RenderRouteSnapToRoad;
        if (routeRendererSnapToRoad) {
          if (isSelected && hoverStateRouteViewsEnabled.SNAP_TO_ROAD) {
            routeRendererSnapToRoad.show();
          } else {
            routeRendererSnapToRoad.hide();
          }
        }

        // toggle directions service route visibility using the classObject of RenderRouteDirectionsService
        const routeRendererDirectionsService = tripData.views
          .ROUTE_DIRECTIONS_SERVICE.classObject as RenderRouteDirectionsService;
        if (routeRendererDirectionsService) {
          if (isSelected && hoverStateRouteViewsEnabled.DIRECTIONS_SERVICE) {
            routeRendererDirectionsService.show();
          } else {
            routeRendererDirectionsService.hide();
          }
        }

        // toggle default route visibility using the classObject of RenderRouteDefault
        const routeRendererDefault = tripData.views.ROUTE_DEFAULT
          .classObject as RenderRouteDefault;
        if (routeRendererDefault) {
          if (isSelected && hoverStateRouteViewsEnabled.DEFAULT) {
            routeRendererDefault.show();
          } else {
            routeRendererDefault.hide();
          }
        }
      });

      break;

    case "routeViewsChange":
      const routeViewsEnabled = value as TMapRouteViewsEnabled;
      props.mapRef.current.routeViewsEnabled = routeViewsEnabled;

      // toggle route views based on the enabled/disabled state
      props.mapRef.current?.objects?.mTripsData.forEach((tripData) => {
        // toggle route renderer - point to point
        const routeRendererP2P = tripData.views.ROUTE_POINT_TO_POINT
          .classObject as RenderRoutePointToPoint;
        if (routeRendererP2P) {
          if (routeViewsEnabled.POINT_TO_POINT) {
            routeRendererP2P.show();
          } else {
            routeRendererP2P.hide();
          }
        }

        // toggle route renderer - snap to road
        const routeRendererSnapToRoad = tripData.views.ROUTE_SNAP_TO_ROAD
          .classObject as RenderRouteSnapToRoad;
        if (routeRendererSnapToRoad) {
          if (routeViewsEnabled.SNAP_TO_ROAD) {
            routeRendererSnapToRoad.show();
          } else {
            routeRendererSnapToRoad.hide();
          }
        }

        // toggle route renderer - directions service
        const routeRendererDirectionsService = tripData.views
          .ROUTE_DIRECTIONS_SERVICE.classObject as RenderRouteDirectionsService;
        if (routeRendererDirectionsService) {
          if (routeViewsEnabled.DIRECTIONS_SERVICE) {
            routeRendererDirectionsService.show();
          } else {
            routeRendererDirectionsService.hide();
          }
        }

        // toggle route renderer - default
        const routeRendererDefault = tripData.views.ROUTE_DEFAULT
          .classObject as RenderRouteDefault;
        if (routeRendererDefault) {
          if (routeViewsEnabled.DEFAULT) {
            routeRendererDefault.show();
          } else {
            routeRendererDefault.hide();
          }
        }
      });

      break;

    case "tripReplayEvents":
      if (APP_CONFIG.DEBUG.MAPS) {
        console.log("tripReplayEvents", value);
        console.log("PP", props.mapRef.current?.objects?.mPushpins);
      }

      const {
        tripIndex: tripReplayIndex,
        completeCallback,
        replayPercChangeCallback,
      } = value as {
        tripIndex: number;
        completeCallback: () => void;
        replayPercChangeCallback: (perc: number | null) => void;
      };
      const tripReplaySpeed = 350; // ms

      const tripPushpins = props.mapRef.current?.objects?.mPushpins;

      if (!tripPushpins) return;

      // Hide all pushpins
      tripPushpins.forEach((pushpinObject) => {
        // pushpinObject.pushpin.setVisible(false);
        pushpinObject.marker.setMap(null);
      });

      // the replay only walks the events the Event Filter is showing -
      // otherwise it put every hidden pin back on the map as it went, which
      // undid the filter (most visibly with "All Event" off)
      const replayPushpins = tripPushpins.filter((pushpinObject) =>
        statusCodeIsPinned(
          props.selectedStatusCodes,
          pushpinObject.pointData.statuscode
        )
      );

      if (replayPushpins.length === 0) {
        completeCallback?.();
        return;
      }

      // Show them one by one to simulate the trip replay
      let pushpinIndex = 0;
      const tripReplayInterval = setInterval(() => {
        if (pushpinIndex >= replayPushpins.length) {
          clearInterval(tripReplayInterval);
          completeCallback?.();
          return;
        }

        // replayPushpins[pushpinIndex].pushpin.setVisible(true);
        replayPushpins[pushpinIndex].marker.setMap(props.mapRef.current.map);
        pushpinIndex++;
        replayPercChangeCallback?.(
          (100 * pushpinIndex) / replayPushpins.length
        );
      }, tripReplaySpeed);
      break;

    case "tripPointSelectionChange":
      const selectedTripPoint = value as VehicleMapDataPoint | null;
      const tripPointRecordId = selectedTripPoint?.recordid || null;

      // Hide all pushpins except the selected one
      props.mapRef.current?.objects?.mPushpins.forEach((pushpinObject) => {
        pushpinObject.marker.setMap(
          // this new code show only the points that match the current filters
          tripPointRecordId === null
            ? statusCodeIsPinned(
                props.selectedStatusCodes,
                pushpinObject.pointData.statuscode
              )
              ? props.mapRef.current.map
              : null
            : pushpinObject.recordId === tripPointRecordId
              ? props.mapRef.current.map
              : null
          // the code below was resetting (not filtering) the applicable points
          // tripPointRecordId === null ||
          //   pushpinObject.recordId === tripPointRecordId
          //   ? props.mapRef.current.map
          //   : null
        );
      });

      const selectedPointPushpinObject =
        props.mapRef?.current?.objects?.mPushpins.find(
          (pushpin) => pushpin.recordId === tripPointRecordId
        );

      if (selectedPointPushpinObject) {
        if (APP_CONFIG.DEBUG.MAPS)
          console.log("selectedPointPushpinObject", selectedPointPushpinObject);
        // Center the map to the selected trip point
        props.mapRef?.current?.map.setOptions({
          center: new google.maps.LatLng(
            typeof selectedPointPushpinObject.marker.position.lat
              ? selectedPointPushpinObject.marker.position.lat
              : selectedPointPushpinObject.marker.position.lat(),
            typeof selectedPointPushpinObject.marker.position.lng
              ? selectedPointPushpinObject.marker.position.lng
              : selectedPointPushpinObject.marker.position.lng()
          ),
        });
        props.mapRef?.current?.map.setZoom(APP_CONFIG.MAPS.DEFAULTS.EVENT_ZOOM);
      } else {
        // Center the map to all visible pushpins
        mapCenterToDataPoints(props, props.dataPoints);
      }
      break;

    default:
      break;
  }
};

// center the map to the visible pushpins
// const mapCenterToDataPoints: TMapOperations = async(props, value) => {
//   //const Microsoft = (window as any).Microsoft;
//   const google = (window as any).google;
//   const { LatLngBounds } = await google.maps.importLibrary("core");
//   if (!props.dataPoints || props.dataPoints.length < 1) return;
//   //props.mapRef.current.map?.setView({
//     // bounds: Microsoft.Maps.LocationRect.fromLocations(
//     //   // visibleDataPoints
//     //   props.dataPoints
//     //     .reduce(
//     //       (t, i, index) =>
//     //         // filtering trip points based on selectedTripIndex
//     //         props?.selectedTripIndex !== null &&
//     //         index !== props?.selectedTripIndex
//     //           ? t
//     //           : [...t, ...i],
//     //       []
//     //     ) // flatten the array
//     //     .map(
//     //       (dataPoint) =>
//     //         new Microsoft.Maps.Location(dataPoint.latitude, dataPoint.longitude)
//     //     )
//     // ),
//     // padding: 30, // 5,
//     // animate: true,
//   //});
// };

const mapCenterToDataPoints: TMapOperations = async (props, value) => {
  const google = window.google;
  const googleMapsCore: any = await google?.maps?.importLibrary?.("core");
  if (!googleMapsCore || !googleMapsCore.LatLngBounds) return;
  const { LatLngBounds } = googleMapsCore;
  if (!props.dataPoints || props.dataPoints.length < 1) return;
  const bounds = new LatLngBounds();
  props.dataPoints
    .reduce((acc, trip, index) => {
      if (
        props?.selectedTripIndex === null ||
        index === props?.selectedTripIndex
      ) {
        acc.push(...trip);
      }
      return acc;
    }, [])
    .forEach((dataPoint) => {
      bounds.extend(
        new google.maps.LatLng(dataPoint.latitude, dataPoint.longitude)
      );
    });
  if (!bounds.isEmpty()) {
    props.mapRef.current.map?.fitBounds(bounds, 20);
  }
};

export const mapVehicleStateIconSlug = (
  dataPoint: TVehicleDataPoint | undefined
) => {
  // if(!!dataPoint && dataPoint.coords.length > 2) return 'driving';
  // return !!dataPoint && dataPoint.is_active ? 'idle_active' : 'idle_inactive';
  return "mapVehicleStateIconSlug";
};

const mapGetPushpinCountWithinMapBounds = (props: TMapOperationsProps) => {
  const mapBounds = props.mapRef?.current?.map?.getBounds();
  if (!mapBounds) return 0;
  const pushpins = props.mapRef.current.objects.mPushpins;
  return pushpins.filter((pushpinObject) =>
    mapPushpinLiesWithinMapBounds(
      pushpinObject.marker,
      props.mapRef?.current?.map
    )
  ).length;
};

const mapPushpinLiesWithinMapBounds = (pushpin: any, mapObj: any) => {
  const mapBounds = mapObj.getBounds();
  if (!mapBounds) return false;
  return mapBounds.contains(pushpin.getLocation()) && pushpin.getVisible();
};

const handleMapViewChangeEnd = (props: TMapOperationsProps) => {
  const pushpinCountWithinMapBounds = mapGetPushpinCountWithinMapBounds(props);
  const showInfoboxes =
    pushpinCountWithinMapBounds <=
    APP_CONFIG.MAPS.SHOW_INFOBOX_PUSHPIN_THRESHOLD;
  if (APP_CONFIG.DEBUG.MAPS)
    console.log(
      `viewchangeend count: ${pushpinCountWithinMapBounds} showInfoboxes: ${showInfoboxes}`
    );

  // update mapState with new center and zoom
  if (!!props.mapRef.current.map?.getCenter) {
    props?.onViewChangeEnd?.(
      {
        latitude: props.mapRef.current.map?.getCenter().latitude,
        longitude: props.mapRef.current.map?.getCenter().longitude,
      },
      props.mapRef.current.map.getZoom()
    );
  }
};
